import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { Globe, Search, ChevronDown, Check, X } from 'lucide-react';
import { getTimezoneListWithOffsets } from 'utils/timezoneHelper';

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const SelectButton = styled.button`
  width: 100% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  background-color: ${props => props.isStake ? '#0f212e' : '#1e2024'} !important;
  border: 1px solid ${props => props.isOpen ? '#1475e1' : (props.isStake ? '#2f4553' : '#3a4142')} !important;
  border-radius: 6px !important;
  padding: 12px 16px !important;
  color: #fff !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  outline: none !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;

  &:hover {
    border-color: ${props => props.isStake ? '#476375' : '#525a5c'} !important;
  }

  &:disabled {
    opacity: 0.6 !important;
    cursor: not-allowed !important;
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background-color: ${props => props.isStake ? '#122129' : '#292d2e'};
  border: 1px solid ${props => props.isStake ? '#2f4553' : '#3a4142'};
  border-radius: 8px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.6);
  z-index: 1050;
  overflow: hidden;
  animation: fadeIn 0.15s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const SearchContainer = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid ${props => props.isStake ? '#2f4553' : '#3a4142'};
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${props => props.isStake ? '#0b161c' : '#1e2024'};
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent !important;
  border: none !important;
  outline: none !important;
  color: #fff !important;
  font-size: 13px !important;
  padding: 4px 0 !important;

  &::placeholder {
    color: #6c7885;
  }
`;

const OptionList = styled.div`
  max-height: 240px;
  overflow-y: auto;
  padding: 4px 0;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #2f4553;
    border-radius: 3px;
  }
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 13px;
  color: ${props => props.isSelected ? '#1fc16b' : '#d5dce2'};
  background-color: ${props => props.isSelected ? (props.isStake ? 'rgba(31, 193, 107, 0.1)' : 'rgba(31, 193, 107, 0.12)') : 'transparent'};
  transition: background 0.15s ease;

  &:hover {
    background-color: ${props => props.isStake ? '#1a2e3b' : '#353a3c'};
    color: #fff;
  }
`;

const OffsetBadge = styled.span`
  font-size: 11px;
  color: #8e969f;
  background: ${props => props.isStake ? '#0f212e' : '#1e2024'};
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid ${props => props.isStake ? '#233744' : '#3a4142'};
  font-family: monospace;
`;

const EmptyMessage = styled.div`
  padding: 24px 16px;
  text-align: center;
  color: #8e969f;
  font-size: 13px;
`;

const SearchableTimezoneSelect = ({ value, onChange, disabled, isStake = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const timezoneList = useMemo(() => getTimezoneListWithOffsets(), []);

  const selectedItem = useMemo(() => {
    return timezoneList.find((tz) => tz.value === value) || {
      value: value || 'Asia/Kolkata',
      label: value ? `${value.replace(/_/g, ' ')}` : 'Asia/Kolkata',
      offset: '',
    };
  }, [timezoneList, value]);

  const filteredTimezones = useMemo(() => {
    if (!searchTerm.trim()) return timezoneList;
    const term = searchTerm.trim().toLowerCase();
    return timezoneList.filter((tz) => tz.searchKey.includes(term));
  }, [timezoneList, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    } else {
      setSearchTerm('');
    }
  }, [isOpen]);

  const handleSelect = (tzValue) => {
    onChange(tzValue);
    setIsOpen(false);
  };

  return (
    <Container ref={containerRef}>
      <SelectButton
        type="button"
        isStake={isStake}
        isOpen={isOpen}
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <ButtonContent>
          <Globe size={16} style={{ color: '#1475e1', flexShrink: 0 }} />
          <span>{selectedItem.label}</span>
        </ButtonContent>
        <ChevronDown
          size={16}
          style={{
            color: '#8e969f',
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'none',
          }}
        />
      </SelectButton>

      {isOpen && (
        <DropdownMenu isStake={isStake}>
          <SearchContainer isStake={isStake}>
            <Search size={14} style={{ color: '#8e969f' }} />
            <SearchInput
              ref={searchInputRef}
              type="text"
              placeholder="Search country, city, timezone (e.g. Kolkata, London, GMT+5)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <X
                size={14}
                style={{ color: '#8e969f', cursor: 'pointer' }}
                onClick={() => setSearchTerm('')}
              />
            )}
          </SearchContainer>

          <OptionList>
            {filteredTimezones.length > 0 ? (
              filteredTimezones.map((tz) => (
                <OptionItem
                  key={tz.value}
                  isStake={isStake}
                  isSelected={tz.value === value}
                  onClick={() => handleSelect(tz.value)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {tz.value === value && <Check size={14} style={{ color: '#1fc16b' }} />}
                    <span>{tz.value.replace(/_/g, ' ')}</span>
                  </div>
                  {tz.offset && <OffsetBadge isStake={isStake}>{tz.offset}</OffsetBadge>}
                </OptionItem>
              ))
            ) : (
              <EmptyMessage>No timezones match "{searchTerm}"</EmptyMessage>
            )}
          </OptionList>
        </DropdownMenu>
      )}
    </Container>
  );
};

export default SearchableTimezoneSelect;
